import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const AdminOrderSummary = () => {
  const { id } = useParams(); // Retrieve the ID from the route params
  const [order, setOrder] = useState(null); // State to store order details
  const [loading, setLoading] = useState(true); // State for loading indicator
  const [error, setError] = useState(null); // State for error handling

  // State for managing updates to order and payment statuses
  const [updatedDeliverystatus, setUpdatedDeliverystatus] = useState('');
  const [updatedPaymentstatus, setUpdatedPaymentstatus] = useState('');
  const [showDeliveryStatusDropdown, setShowDeliveryStatusDropdown] = useState(false); // State to toggle delivery status dropdown
  const [showPaymentStatusDropdown, setShowPaymentStatusDropdown] = useState(false); // State to toggle payment status dropdown

  useEffect(() => {
    // Fetch order details by ID
    const fetchOrder = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const email = user.email;

        const response = await axios.get(`https://citi-mobile-app.onrender.com/admin/fulldetails/${id}/${email}`, {
          params: { email }, // Send the email as a query parameter
        });
        setOrder(response.data.order);
        setUpdatedDeliverystatus(response.data.order.deliverystatus);
        setUpdatedPaymentstatus(response.data.order.paymentstatus);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch order details');
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  // Function to update the order and payment statuses
  const handleUpdate = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const email = user.email;

      const response = await axios.put(
        `https://citi-mobile-app.onrender.com/admin/update-order-status/${id}`,
        {
          email,
          deliverystatus: updatedDeliverystatus,
          paymentstatus: updatedPaymentstatus,
        }
      );

      // If the update is successful, refresh the order data
      if (response.status === 200) {
        setOrder((prevOrder) => ({
          ...prevOrder,
          deliverystatus: updatedDeliverystatus,
          paymentstatus: updatedPaymentstatus,
        }));
      }
    } catch (err) {
      setError('Failed to update order details');
    }
  };

  // Display loading spinner if the data is still being fetched
  if (loading) {
    return <div>Loading...</div>;
  }

  // Display error message if there is an issue fetching the data
  if (error) {
    return <div>{error}</div>;
  }

  // Check if the order data exists
  if (!order) {
    return <div>No order found</div>;
  }

  return (
    <div className="flex max-sm:flex-col max-sm:mt-20 justify-between max-w-5xl mx-auto p-6 max-sm:p-0 bg-gray-50 max-sm:bg-white ml-60 max-sm:ml-0">
      {/* Products Section */}
      <div className="w-[45%] max-sm:w-full bg-white p-6 rounded-md shadow-md h-fit">
        <h2 className="text-xl font-bold mb-4">Products</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {order.products.map((product, index) => (
            <div key={index} className="flex items-center">
              <img
                src={product.image}
                alt={product.title}
                className="h-20 w-20 object-cover mr-4"
              />
              <div>
                <p className="font-semibold">{product.title}</p>
                <p className="font-semibold">{product.price}</p>
                <p className="text-blue-600">{product.cartonOption}</p>
                <p className="text-blue-600">{product.quantity}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary Section */}
      <div className="w-[45%] max-sm:w-full max-sm:mt-5 bg-white p-6 h-fit rounded-md shadow-md">
        <div className="mb-6">
          <p className="text-gray-600">Customer's Name:</p>
          <p className="font-semibold">{order.fullname}</p>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">Delivery Details</p>
          <p>Delivery Address: {order.address}</p>
          <p>Delivery to: {order.deliveryTo}</p>
          <p>Phone Number: {order.phoneNumber}</p>
        </div>
        <div className="mb-6">
          <p className="text-gray-600">Total Amount:</p>
          <p className="font-semibold">{order.totalAmount}</p>
        </div>

        {/* Order Status Section with Select Option */}
        <div className="mb-6">
          <p className="text-gray-600">Order Status</p>
          {order.deliverystatus !== 'delivered' && ( // Only show dropdown if not delivered
            showDeliveryStatusDropdown ? (
              <select
                value={updatedDeliverystatus}
                onChange={(e) => setUpdatedDeliverystatus(e.target.value)}
                onBlur={() => setShowDeliveryStatusDropdown(false)} // Hide dropdown on blur
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="processing">Processing</option>
                <option value="delivered">Delivered</option>
              </select>
            ) : (
              <span
                className="bg-green-100 text-green-600 py-1 px-3 rounded-full text-sm cursor-pointer"
                onClick={() => setShowDeliveryStatusDropdown(true)} // Show dropdown on click
              >
                {order.deliverystatus}
              </span>
            )
          )}
        </div>

        {/* Payment Status Section with Select Option */}
        <div className="mb-6">
          <p className="text-gray-600">Payment Status</p>
          {order.paymentstatus !== 'paid' && ( // Only show dropdown if not paid
            showPaymentStatusDropdown ? (
              <select
                value={updatedPaymentstatus}
                onChange={(e) => setUpdatedPaymentstatus(e.target.value)}
                onBlur={() => setShowPaymentStatusDropdown(false)} // Hide dropdown on blur
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            ) : (
              <span
                className="bg-green-100 text-green-600 py-1 px-3 rounded-full text-sm cursor-pointer"
                onClick={() => setShowPaymentStatusDropdown(true)} // Show dropdown on click
              >
                {order.paymentstatus}
              </span>
            )
          )}
        </div>

        {/* Update Button */}
        {(showDeliveryStatusDropdown || showPaymentStatusDropdown) && (
          <button
            onClick={handleUpdate}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Update Status
          </button>
        )}

        {/* Date Ordered Section */}
        <div className="mb-6">
          <p className="text-gray-600">Date Ordered</p>
          <span>{order.date}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderSummary;
